// src/rms/pages/AgreementView.jsx
// ============================================================================
// The signed record
// ============================================================================
// What the customer signed, who signed it, when, and how it was consented to --
// on one page that prints.
//
// Two ways to get it out, and they are not the same artefact.
//
// PRINT lays out this page. No library in the bundle, same stored characters as
// the screen by construction, and available whether or not a PDF was ever
// rendered -- which matters for anything signed at the counter, where nothing
// generates one.
//
// DOWNLOAD PDF hands over the file the signing function rendered server-side,
// where its weight costs the browser nothing. That is the one to attach to an
// email or file with an insurer. It exists only for remotely signed documents.
//
// ----------------------------------------------------------------------------
// The integrity line is not decoration
// ----------------------------------------------------------------------------
// body_sha256 is recomputed by Postgres on every write, so it cannot be forged
// through the app. Showing the check here answers a question a dispute actually
// asks -- "is this the text that was signed, or has the database been edited
// since?" -- and answers it in front of the person asking.
// ============================================================================

import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useRms } from "../context/RmsContext";
import db from "../data/index";
import agreementDocumentService from "../data/agreementDocumentService";
import { signedUrlFor, downloadUrlFor, niceFileName } from "../data/documentStorage";
import AgreementReader from "../components/AgreementReader";
import { PageHeader, Banner, StatusPill, formatDateTime } from "../components/ui";

export default function AgreementView() {
  const { documentId } = useParams();
  const { operator, t } = useRms();

  const [doc, setDoc] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  const [signatureUrl, setSignatureUrl] = useState(null);
  const [signatureProblem, setSignatureProblem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = useCallback(async () => {
    const found = await agreementDocumentService.getDocument(documentId);
    const row = found.data;
    setLoading(false);
    if (!row) return;

    setDoc(row);
    setIntegrity((await agreementDocumentService.verifyIntegrity(row)).data);
    setCustomer((await db.get("customers", row.customer_id)).data);

    if (row.signature_storage_path) {
      // Old rows hold a base64 data: URL in this column rather than a path --
      // see documentStorage.uploadSignature. Render those directly instead of
      // asking storage for a signed URL to something that was never uploaded.
      if (String(row.signature_storage_path).startsWith("data:")) {
        setSignatureUrl(row.signature_storage_path);
      } else {
        const url = await signedUrlFor(row.signature_storage_path, 300);
        if (url.success) setSignatureUrl(url.data);
        else setSignatureProblem(url.error);
      }
    }
  }, [documentId]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadPdf = async () => {
    setPdfBusy(true);
    const name = niceFileName([doc.title, doc.signer_name, (doc.signed_at || "").slice(0, 10)]);
    const url = await downloadUrlFor(doc.pdf_storage_path, name);
    setPdfBusy(false);
    if (!url.success) {
      setSignatureProblem(url.error);
      return;
    }
    // A real navigation rather than an <a download>: the file is on another
    // origin, so the download attribute would be ignored and the header from
    // the signed URL is what actually names it.
    window.location.assign(url.data);
  };

  if (loading) return <p style={{ color: "var(--rms-muted-strong)" }}>{t("common.loading", "Loading…")}</p>;

  if (!doc) {
    return (
      <>
        <PageHeader
          title={t("agreementView.notFound", "Not found")}
          subtitle={t("agreementView.notFoundHelp", "That document no longer exists.")}
        />
        <Link className="rms-btn rms-btn--ghost" to="/rms/reservations">
          {t("agreementView.back", "Back to bookings")}
        </Link>
      </>
    );
  }

  const signerLine = [doc.signer_name, doc.signer_email].filter(Boolean).join(" · ");

  return (
    <div className="rms-printable">
      <div className="rms-noprint">
        <PageHeader
          title={doc.title}
          subtitle={`${customer ? `${customer.first_name} ${customer.last_name || ""}`.trim() : ""}`}
          actions={
            <>
              {/* The PDF the signing function rendered, offered under a name
                  somebody can find again. Object paths are uuids on purpose;
                  download names should never have been. */}
              {doc.pdf_storage_path ? (
                <button
                  type="button"
                  className="rms-btn rms-btn--ghost"
                  onClick={downloadPdf}
                  disabled={pdfBusy}
                >
                  {pdfBusy ? t("common.working", "Working…") : t("agreementView.downloadPdf", "Download PDF")}
                </button>
              ) : null}{" "}
              <button type="button" className="rms-btn rms-btn--primary" onClick={() => window.print()}>
                {t("agreementView.print", "Print or save as PDF")}
              </button>
            </>
          }
        />

        <div style={{ marginBottom: 14, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <StatusPill status={doc.status} />
          {integrity?.checked ? (
            integrity.intact ? (
              <span style={{ fontSize: 12, color: "var(--rms-muted-strong)" }}>
                {t("agreementView.intact", "The stored text still matches its fingerprint.")}
              </span>
            ) : (
              <span style={{ fontSize: 12, color: "var(--rms-danger)" }}>
                {t("agreementView.tampered", "This text no longer matches the fingerprint recorded when it was signed.")}
              </span>
            )
          ) : null}
        </div>

        {signatureProblem ? <Banner tone="warn">{signatureProblem}</Banner> : null}
      </div>

      {/* Everything below prints. */}
      <div className="rms-card rms-card-pad rms-print-plain">
        <h1 style={{ fontSize: 18, margin: "0 0 4px" }}>{doc.title}</h1>
        <p style={{ fontSize: 12, color: "var(--rms-muted)", margin: "0 0 18px" }}>
          {operator.business_name}
          {doc.signed_at ? ` · ${formatDateTime(doc.signed_at)}` : ""}
        </p>

        <AgreementReader body={doc.body_snapshot} />

        {doc.status === "signed" ? (
          <div
            style={{
              marginTop: 26,
              paddingTop: 16,
              borderTop: "1px solid var(--rms-border)",
              breakInside: "avoid",
            }}
          >
            <h3 style={{ fontSize: 13.5, margin: "0 0 10px" }}>
              {t("agreementView.signature", "Signature")}
            </h3>

            {signatureUrl ? (
              <img
                src={signatureUrl}
                alt={t("agreementView.signatureAlt", "Signature")}
                style={{
                  display: "block",
                  maxWidth: 320,
                  height: "auto",
                  background: "#fff",
                  border: "1px solid var(--rms-border)",
                  borderRadius: 6,
                  marginBottom: 10,
                }}
              />
            ) : null}

            <dl className="rms-agreement-audit">
              <dt>{t("agreementView.signedBy", "Signed by")}</dt>
              <dd>{signerLine || "—"}</dd>

              <dt>{t("agreementView.signedAt", "Signed")}</dt>
              <dd>{formatDateTime(doc.signed_at)}</dd>

              <dt>{t("agreementView.consented", "Agreed to sign electronically")}</dt>
              <dd>{doc.signer_consented_at ? formatDateTime(doc.signer_consented_at) : "—"}</dd>

              <dt>{t("agreementView.where", "Signed at")}</dt>
              <dd>
                {doc.signed_via === "counter"
                  ? t("agreementView.atCounter", "In person, at the counter")
                  : t("agreementView.remotely", "Remotely, from a link we sent")}
              </dd>

              {doc.signer_ip ? (
                <>
                  <dt>{t("agreementView.fromAddress", "From")}</dt>
                  <dd>{doc.signer_ip}</dd>
                </>
              ) : null}

              <dt>{t("agreementView.fingerprint", "Document fingerprint")}</dt>
              <dd style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 11, wordBreak: "break-all" }}>
                {doc.body_sha256}
              </dd>
            </dl>
          </div>
        ) : null}
      </div>
    </div>
  );
}
